import { TaskEither } from "@morphism/fp";
import React from "react";
import styled from "styled-components";

import * as Color from "~/Color";
import { gql, Graph } from "~/Graph";
import * as Layout from "~/Layout";
import * as Typography from "~/Typography";

import { Lifecycle } from "./Lifecycle";
import { Media } from "./Media";

export type Signature = {
  readonly signature: { readonly uri?: string } & Lifecycle.Submitted;
};
export function Signature({ signature }: Partial<Signature>) {
  return (
    <Signature.Container>
      <Signature.Content>
        {signature?.uri ? (
          <Signature.Image>
            <img src={signature.uri} />
          </Signature.Image>
        ) : (
          <Typography.Component muted>None available</Typography.Component>
        )}
      </Signature.Content>
    </Signature.Container>
  );
}

export namespace Signature {
  export namespace Fragment {
    export const document = () => gql`
      ${Lifecycle.Submitted.Fragment.document()}
      ${Media.Image.Fragment.document()}

      fragment Signature on Signature__HasOne {
        signature {
          id
          ...Submitted
          image {
            ...Image
          }
        }
      }
    `;
  }

  export const decode = (encoded: Fragment): Signature => {
    return {
      signature: {
        uri: encoded?.signature?.image?.file?.uri,
        ...Lifecycle.Submitted.decode(encoded.signature),
      },
    };
  };

  export namespace Mutate {
    export const create = ({ signature }: Signature) => {
      const lifecycle = {
        submitted: {
          date: signature.submitted.dateTime,
          actor: {
            actorType: "HUMAN",
            actorName: signature.submitted.name,
            userID: signature.submitted.user?.id,
          },
        },
      };
      const image = signature.uri
        ? {
            uri: signature.uri,
            lifecycle,
          }
        : undefined;
      const [mutate, rest] = Graph.useMutation<Graph.Generated.Mutation>(
        gql`
          mutation CreateSignature(
            $image: Media__Type__Image__CreateInput
            $lifecycle: Lifecycle__CreateInput!
          ) {
            signatures {
              create(input: { image: $image, lifecycle: $lifecycle }) {
                ... on Signature {
                  id
                  image {
                    file {
                      uri
                    }
                  }
                  ...Submitted
                }
                ...Error
              }
            }
          }
          ${Lifecycle.Submitted.Fragment.document()}
          ${Graph.Errors.Fragment.document()}
        `,
        { variables: { image, lifecycle } }
      );
      return [TaskEither.fromTask(mutate), rest] as const;
    };
  }

  export type Fragment = Graph.Generated.SignatureFragment;

  export const Container = styled.div`
    background: ${() => Color.almostWhite()};
    padding: ${() => Layout.Inline.padding()};
    page-break-inside: avoid;
    border-radius: ${() => Layout.Inline.padding()};
    border: 1px solid ${() => Color.veryWhiteGrey()};
  `;

  export const Content = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${Layout.Block.padding()};
  `;

  export const Image = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 130px;
    width: 100%;
    & > img {
      height: 100%;
    }
  `;

  export const SVG = () => (
    <svg
      width="246"
      height="68"
      viewBox="0 0 246 68"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 48.82C23.3404 80.6669 84.8245 69.9929 89.0793 28.6051C91.4878 5.1771 67.8896 -9.997 53.27 11.4224C50.7066 15.178 44.8803 28.3666 52.8368 29.1826C76.246 31.5836 99.2983 24.3313 122.578 24.1289C135.914 24.0129 140.05 29.5402 140.05 42.2501C140.05 50.2798 135.122 56.3123 126.405 53.7293C114.531 50.2111 119.425 34.8774 127.343 30.6266C140.512 23.5568 157.378 26.3523 171.744 23.4791C179.18 21.992 185.676 18.3202 193.042 16.6205C195.736 15.9989 194.63 29.6534 194.63 31.7817C194.63 38.0356 200.437 37.5088 205.026 40.734C209.835 44.1134 208.431 44.984 207.698 50.1195C206.553 58.1341 241.178 35.8247 245.312 35.8247"
        stroke="#041122"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
